import React, { useEffect, useContext } from 'react'
import classNames from 'classnames'
import { Box, Typography } from 'transfix-ui/core'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { brandColors } from 'transfix-ui/theme'
import { shadows } from 'transfix-ui/theme/shadows'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import AdditionalStopsDivider from 'apps/marketplace/pages/spot-quote-tool/components/AdditionalStopsDivider'
import { CostReferenceCard } from 'apps/marketplace/pages/spot-quote-tool/components/CostReferenceCard'
import { CloseIcon } from 'transfix-icons'
import { Card, CardHeader } from 'transfix-ui'
import { CostCalculationCard } from '../../../components/CostCalculationCard'
import { SpotRulesCard } from '../../../components/SpotRulesCard'
import { IPriceRequestDetailsFragment } from '../../../graphql/PriceRequestDetailsFragment'
import { formatPriceRequestSource } from '../../utils/formatFocusPanelContents'
import { formatPuDelTime } from '../../utils/formatHistoricalQuotesTableContents'
import { RequestedByAvatar } from '../RequestedByAvatar'
import { CostReferenceSection } from './CostReferenceSection'
import { QuoteDetailsTable } from './QuoteDetailsTable'
import { SpotRulesSection } from './SpotRulesSection'
import { SubmittedQuoteCard } from './SubmittedQuoteCard'
import { TransfixQuotesCard } from './TransfixQuotesCard'
import { usePriceRequestAppliedRules } from './hooks/usePriceRequestAppliedRules'

interface IFocusedQuotePanelProps {
  priceRequest: IPriceRequestDetailsFragment
  setFocusedQuoteId: React.Dispatch<React.SetStateAction<string | null>>
}

const useStyles = makeStyles(theme => ({
  panel: {
    width: '100%',
    marginLeft: '15px',
    marginTop: '45px',
    overflowY: 'auto',
    maxHeight: '95vh',
    boxShadow: shadows[3],
    padding: theme.spacing(3),
  },
  costReference: {
    marginLeft: theme.spacing(-3),
    marginRight: theme.spacing(-3),
    marginBottom: theme.spacing(-3),
  },
  close: {
    float: 'right',
  },
  topCard: {
    background: brandColors.coolGray0,
  },
  quotes: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  quoteCard: {
    width: '50%',
    height: '280px',
    overflow: 'auto',
  },
  spotRulesCard: {
    padding: 'unset',
    marginTop: theme.spacing(2),
  },
  modalClass: {
    left: '-42.3vw',
  },
  costCard: {
    marginTop: theme.spacing(3),
  },
  whiteSpace: {
    height: '150px',
  },
}))

export function FocusedQuotePanel({ priceRequest, setFocusedQuoteId }: IFocusedQuotePanelProps) {
  const classes = useStyles()

  const { getFlag } = useContext(FeatureFlagsContext)
  const { enabled: sqtAppliedRulesDataEnabled } = getFlag('sqtAppliedRulesData')
  const { enabled: sqtObjectiveFourEnabled } = getFlag('sqtObjectiveFour')
  const closePanelHandler = () => {
    setFocusedQuoteId(null)
  }
  const closeClass = classNames('icon-hoverable', 'pointer', classes.close)
  const createdDate = formatPuDelTime(priceRequest.createdAt)[0]

  // bug with react-stickynode where the panel may not be rendered in the correct position
  // until the user scrolls - here we scroll by just one pixel on render to get the panel
  // in the correct position
  useEffect(() => {
    window.scrollBy(0, 1)
  }, [])

  const { spotRate } = usePriceRequestAppliedRules(priceRequest)
  const {
    metadata: {
      additionalStops,
      appliedCostControlConfig,
      isDoNotBid,
      spotBiddingRules,
      strikePriceRules,
    },
  } = spotRate

  let appliedRulesPresent = false

  if (strikePriceRules?.appliedRules) {
    appliedRulesPresent = strikePriceRules?.appliedRules.length > 0
  }

  if (spotBiddingRules?.appliedRules && !appliedRulesPresent) {
    appliedRulesPresent = spotBiddingRules?.appliedRules.length > 0
  }

  return (
    <div className={classes.panel}>
      <Card className={classes.topCard}>
        <CardHeader
          actionButton={
            <span
              aria-label='Close Quote'
              className={closeClass}
              data-test='close-focused-quote'
              role='button'
              tabIndex={0}
              onClick={closePanelHandler}
              onKeyPress={closePanelHandler}>
              <CloseIcon className='icon large text-secondary' />
            </span>
          }>
          {additionalStops && additionalStops.length > 0 && sqtObjectiveFourEnabled ? (
            <Box display='flex'>
              <Box>
                <Typography variant='h3'>
                  {priceRequest.shipperApplication?.name} | {priceRequest.pickupCity},{' '}
                  {priceRequest.pickupStateCode} ({priceRequest.pickupZipcode})
                </Typography>
              </Box>
              <Box>
                <AdditionalStopsDivider stops={additionalStops} />
              </Box>
              <Box>
                <Typography variant='h3'>
                  {priceRequest.deliveryCity}, {priceRequest.deliveryStateCode} (
                  {priceRequest.deliveryZipcode})
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant='h3'>
              {priceRequest.shipperApplication?.name} | {priceRequest.pickupCity},{' '}
              {priceRequest.pickupStateCode} ({priceRequest.pickupZipcode}){' - '}
              {priceRequest.deliveryCity}, {priceRequest.deliveryStateCode} (
              {priceRequest.deliveryZipcode})
            </Typography>
          )}
        </CardHeader>
        Created on {createdDate} by {formatPriceRequestSource(priceRequest.source)}{' '}
        <RequestedByAvatar
          firstName={priceRequest.requestedBy?.firstName}
          lastName={priceRequest.requestedBy?.lastName}
          userType={priceRequest.requestedBy?.userType}
        />
        <QuoteDetailsTable priceRequest={priceRequest} spotRate={spotRate} />
        <div className={classes.quotes}>
          <TransfixQuotesCard
            className={classes.quoteCard}
            allIn={priceRequest.premiums?.quoteWithFuel}
            linehaul={priceRequest.premiums?.quoteWithoutFuel}
            isDoNotBid={isDoNotBid}
          />
          <SubmittedQuoteCard
            className={classes.quoteCard}
            priceRequest={priceRequest}
            spotRate={spotRate}
          />
        </div>
      </Card>
      {!sqtObjectiveFourEnabled && (
        <CostReferenceSection
          priceRequest={priceRequest}
          appliedCostControlConfig={appliedCostControlConfig}
          spotRate={spotRate}
        />
      )}
      {sqtObjectiveFourEnabled && (
        <div className={classes.costReference}>
          <CostReferenceCard spotRate={spotRate} />
        </div>
      )}
      {sqtAppliedRulesDataEnabled && (
        <>
          <div className={classes.costCard}>
            <CostCalculationCard spotRate={spotRate} />
          </div>

          {appliedRulesPresent && (
            <Box mt={2}>
              <SpotRulesCard spotRate={spotRate} modalClassName={classes.modalClass} />
            </Box>
          )}
          {!appliedRulesPresent && <SpotRulesSection />}
        </>
      )}
      <div className={classes.whiteSpace} />
    </div>
  )
}
